// The one place that reads the coverage catalogue for an address form
// (GET /v1/coverage) and maps it to ZoneOption. Only *active* targets are
// offered — an inactive zone is not deliverable.
//
// The call goes through the app's own origin; the BFF proxies it to the private
// API (never called from the browser directly).

import { type HttpApiClient } from "@vana/core";
import { type ZoneOption } from "./zone-option";

// Already camelCased by the response interceptor.
interface CoverageZoneResponse {
  code: string;
  name: string;
  municipalityName: string;
  departmentName: string;
  active: boolean;
  isDirectDelivery?: boolean;
}

interface CoverageResponse {
  data?: { items?: CoverageZoneResponse[] };
}

export interface FetchZoneOptionsOpts {
  /** Also return inactive targets (the full geo catalogue: every registered
   *  municipio/zone, with or without coverage). Default: active only. */
  includeInactive?: boolean;
}

/** The delivery targets an address form can pick from — active only by default.
 *  Throws the raw transport error — each feature wraps it in its own domain error. */
export const fetchZoneOptions = async (
  apiClient: HttpApiClient,
  opts: FetchZoneOptionsOpts = {},
): Promise<ZoneOption[]> => {
  const response = await apiClient.get<CoverageResponse>("v1/coverage");
  return (response.data?.items ?? [])
    .filter((z) => opts.includeInactive || z.active)
    .map((z) => ({
      code: z.code,
      name: z.name,
      municipalityName: z.municipalityName,
      departmentName: z.departmentName,
      isDirectDelivery: z.isDirectDelivery ?? false,
      active: z.active,
    }));
};
